import React, { ChangeEvent, useState } from 'react';
import dayjs from 'dayjs';
import { useFormContext } from '../../../../components/molecules/Form';
import FormInput from '../../../../components/molecules/Form/components/FormInput/FormInput';
import FormsRow from '../../../../components/molecules/Form/components/FormsRow/FormsRow';
import FormTextarea from '../../../../components/molecules/Form/components/FormTextarea/FormTextarea';
import FormDateRange from '../../../../components/molecules/Form/components/FormDateRange/FormDateRange';
import {
  ContentPreview,
  ContentSelectComboBox,
  ListBoxModifier,
  TopContentResult,
} from '../../../../components/molecules/Form/components/ComboBox';
import { Hint } from '../../../../_lib/components/Hint';
import { BlockType } from '../../../../types/api/ElementType';
import { useBlockContext } from '../../../../_lib/BlockFetcher';
import LiveRegion from '../../../../components/atoms/LiveRegion/LiveRegion';
import { useSonTranslation } from '../../../../translation/useSonTranslation';

export default function Information() {
  const { inputs, onChange } = useFormContext();
  const { elementType } = useBlockContext();

  const { title, text, daterange, referencePageId } = inputs;
  const { t } = useSonTranslation('TopHeadlinesForm');
  const [hintMessage, setHintMessage] = useState<string | undefined>(undefined);

  /**
   * Hint messages are added/removed on blur.
   */
  const giveMeAHint = () => {
    const today = dayjs().format('YYYY-MM-DD');
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const endDateValue = daterange?.params?.endDate?.value;

    if (elementType === BlockType.pagemanagement && today > endDateValue) {
      setHintMessage(t('daterange.hint.fromParentPageToArchive'));
    } else if (elementType === BlockType.topheadlinesarchive && today <= endDateValue) {
      setHintMessage(t('daterange.hint.fromArchiveToParentPage'));
    } else if (elementType === BlockType.topheadlines && today > endDateValue) {
      setHintMessage(t('daterange.hint.fromParentPageToArchive'));
    } else {
      setHintMessage(undefined);
    }
  };

  const idSuffix = 'topheadline';
  const comboboxId = `${referencePageId.name}-${idSuffix}`;

  const removeSelectedItemAndFocusCombobox = (a: boolean) => {
    referencePageId.clear(a);
    document.getElementById(comboboxId)?.focus();
  };

  return (
    <>
      <FormsRow>
        <FormInput
          id={`${title.name}-${idSuffix}`}
          key={`${title.name}-${idSuffix}`}
          type={title.type}
          name={title.name}
          required={title.required}
          readOnly={title.readonly || title?.constraints?.readonly}
          labelTitle={t('title')}
          value={title.value}
          minLength={title?.constraints?.minLength}
          maxLength={title?.constraints?.maxLength}
          pattern={title?.constraints?.pattern}
          onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(title.name, event.target.value)}
        />
      </FormsRow>
      <FormsRow>
        <FormTextarea
          id={`${text.name}-${idSuffix}`}
          key={`${text.name}-${idSuffix}`}
          name={text.name}
          required={text.required}
          autoExpand
          labelTitle={t('textAreaTitle')}
          value={text.value}
          maxLength={(text.constraints && text.constraints.maxLength) || 1000}
          onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.name, event.target.value)}
        />
      </FormsRow>
      <FormsRow>
        <ContentSelectComboBox
          id={comboboxId}
          hideLabelAddition
          modifierListBox={ListBoxModifier.RELATIVE} // listbox expands modal dialog
          name={referencePageId.name}
          // @ts-expect-error -- form typing sucks
          suggestUrl={referencePageId.comboBoxUrl?.url}
          defaultInputValue={referencePageId.selected ? referencePageId.selected.title : null}
          labelTitle={t('referencePageId.label')}
          labelHelpText={t('referencePageId.labelHelpText') as string}
          onSelection={(item: TopContentResult | undefined) => {
            if (referencePageId?.setSelected) {
              referencePageId.setSelected(item);
            }
          }}
        />
      </FormsRow>
      {referencePageId.selected && (
        <ContentPreview {...referencePageId.selected} reset={removeSelectedItemAndFocusCombobox} />
      )}
      <FormDateRange
        id={idSuffix}
        input={daterange}
        labelStartDate={t('daterange.from')}
        labelEndDate={t('daterange.to')}
        labelIsAllDay={t('daterange.isAllDay')}
        onBlur={giveMeAHint}
      />
      {/* Live region must be rendered anyway, otherwise hint is not announced in screen readers! */}
      <LiveRegion invisible={false} htmlTag="div">
        {hintMessage && (
          <div className="top-headlines-form__hint">
            <Hint message={hintMessage} />
          </div>
        )}
      </LiveRegion>
    </>
  );
}
